import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react'
import { TableVirtuoso } from 'react-virtuoso';
import RowNoFound from './RowNoFound';

const VirtualizedTable = (props) => {
  const {
    id = '',
    tableData = [],
    columnDefs = [],
    totalColumnDef = [],
    tableLayout = 'fixed',
    tableWidth = '100%',
    tableClassName = '',
    tableHeaderContent = null,
    tableBodyContent = null,
    matches = [],
    currentIndex = 0
  } = props
  const virtuosoRef = useRef()
  const containerRef = useRef()
  const matchSet = useMemo(() => {
    return new Set(matches.map(m => `${m.rowIndex}-${m.field}`));
  }, [matches]);
  useEffect(() => {
    if (!matches.length) return;

    const currentMatch = matches[currentIndex];
    if (!currentMatch) return;

    const { rowIndex, field, tableId } = currentMatch;
    // 🔽 Vertical scroll
    if (tableId === id) {
      virtuosoRef.current?.scrollToIndex({
        index: rowIndex,
        behavior: 'smooth',
        align: 'center',
      });

      // 🔁 Horizontal scroll (SCOPED!)
      requestAnimationFrame(() => {
        const row = containerRef.current?.querySelector(
          `[data-row-index="${rowIndex}"]`
        );

        if (!row) return;

        const rowData = tableData[rowIndex];
        const cols = rowData?.isTotalRow ? totalColumnDef : columnDefs;

        const colIndex = cols.findIndex(c => c.field === field);
        const cell = row.children[colIndex];

        if (cell) {
          cell.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest',
          });
        }
      });
    }

  }, [currentIndex, matches, columnDefs, totalColumnDef, tableData]);
  const VirtuosoTableComponents = {
    Scroller: React.forwardRef((props, ref) => (
      <div {...props} ref={ref} />
    )),
    Table: (props) => (
      <Table {...props} size="small" stickyHeader
        className={`tableCommomSection ${tableClassName}`.trim()}
        style={{
          tableLayout: tableLayout,
          width: tableWidth,
          border: ' 1px solid #00000033',
          borderRadius: '4px',
          borderCollapse: 'separate',
          borderSpacing: 0,
          overflowX: 'scroll',
          maxWidth: tableWidth === '100%' ? '100%' : 'none',
        }}
      />
    ),
    TableHead,
    TableRow: (props) => {
      const { item, ...rest } = props;
      return (
        <TableRow
          {...rest}
          data-row-index={props['data-index']} // 👈 preserve virtuoso index safely
        />
      );
    },
    TableBody: React.forwardRef((props, ref) => (
      <TableBody {...props} ref={ref} />
    )),
  };
  const headerContent = () => (
    <>
      <TableRow>
        {columnDefs.map((col, index) => (
          <TableCell
            key={`${col.headerName}-${col.field}-${index}`}
            sx={{
              ...col.headerSx,
              fontFamily: 'Inter',
              fontWeight: 600,
              fontStyle: 'Semi Bold',
              fontSize: '12px',
              lineHeight: '17px',
              letterSpacing: '2%',
              color: '#000000'
            }}
          >
            {col.headerName}
          </TableCell>
        ))}
      </TableRow>
    </>
  )
  const bodyContent = (index, obj) => {
    if (tableData.length === 0) {
      return (
        <RowNoFound colSpan={columnDefs?.length} />
      );
    }

    const currentMatch = matches[currentIndex];

    return (
      <>
        {obj.isTotalRow ?
          totalColumnDef.map((col, colIndex) => {

            const key = `${index}-${col.field}`;
            const isMatched = matchSet.has(key);

            const isActive =
              currentMatch &&
              currentMatch.rowIndex === index &&
              currentMatch.field === col.field &&
              currentMatch.tableId === id

            return (
              <TableCell
                key={`totalRow-${col.field}-${index}-${colIndex}`}
                colSpan={col?.colSpan || 1}
                sx={{
                  ...col.headerSx,
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontStyle: 'Semi Bold',
                  fontSize: '12px',
                  lineHeight: '17px',
                  letterSpacing: '2%',
                  color: '#000000',
                  overflowWrap: 'break-word',
                }}
              >
                <span style={{
                  backgroundColor: isActive ? 'orange' : isMatched ? 'yellow' : 'transparent'
                }}>
                  {obj?.[col.field] ?? ''}
                </span>
              </TableCell >
            )
          })
          : obj.isSubHeadingRow ?
            obj.subHeadingData.map((col, colIndex) => (
              <TableCell
                key={`subHeading-${col.field}-${index}-${colIndex}`}
                colSpan={col?.colSpan || 1}
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontStyle: 'Semi Bold',
                  fontSize: '12px',
                  lineHeight: '17px',
                  letterSpacing: '2%',
                  color: '#000000',
                  textAlign: col?.align || 'left',

                }}
              >
                {col.caption || ''} {col.value || ""}
              </TableCell>
            ))
            :
            columnDefs.map((col, colIndex) => {

              const key = `${index}-${col.field}`;
              const isMatched = matchSet.has(key);

              const isActive =
                currentMatch &&
                currentMatch.rowIndex === index &&
                currentMatch.field === col.field;

              return (
                <TableCell
                  key={`dataRow-${col.field}-${index}-${colIndex}`}
                  sx={{
                    ...col.headerSx,
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontStyle: 'normal',
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: '2%',
                    color: '#000000',
                    overflowWrap: 'break-word',

                  }}
                >
                  <span style={{
                    backgroundColor: isActive ? 'orange' : isMatched ? 'yellow' : 'transparent'
                  }}> {obj?.[col.field] ?? ''}</span>
                </TableCell>
              )
            })
        }
      </>
    )
  }
  return (
    <div ref={containerRef} id={id}>
      <TableVirtuoso
        ref={virtuosoRef}
        useWindowScroll
        data={tableData?.length === 0 ? [{}] : tableData}
        components={VirtuosoTableComponents}
        fixedHeaderContent={tableHeaderContent !== null ? tableHeaderContent : headerContent}
        itemContent={tableBodyContent !== null ? tableBodyContent : bodyContent}
      />
    </div>
  )
}

export default VirtualizedTable